/**
 * Product Modal - Fetches and renders product content into gbbMixCustom modal.
 */

const SELECTORS = {
  wrapper: '.gbbMixCustom-modalWrapper',
  content: '.gbbMixCustom-modalContent',
  sectionContent: '.api-product-modal__content',
};

const HIDDEN_CLASS = 'gbbMixCustom-modalWrapper-hidden';

/** @type {Map<string, string>} */
const cache = new Map();

/** @type {AbortController | null} */
let fetchController = null;

/**
 * Opens the product modal and fetches content.
 *
 * @param {string} handle - The product handle to display
 */
export async function openProductModal(handle) {
  if (!handle) return;

  document.querySelector(SELECTORS.wrapper)?.classList.remove(HIDDEN_CLASS);

  try {
    const html = await fetchProductSection(handle);
    renderContent(html);
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      renderError();
    }
  }
}

/**
 * Fetches product section content via Section Rendering API.
 *
 * @param {string} handle - The product handle
 * @returns {Promise<string>} The HTML content
 */
async function fetchProductSection(handle) {
  const cached = cache.get(handle);
  if (cached) return cached;

  fetchController?.abort();
  fetchController = new AbortController();

  const response = await fetch(`/products/${handle}?section_id=api-product-modal`, {
    signal: fetchController.signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const html = await response.text();
  cache.set(handle, html);

  return html;
}

/**
 * Renders fetched content into the modal.
 *
 * @param {string} html - The HTML content to render
 */
function renderContent(html) {
  const container = document.querySelector(SELECTORS.content);
  if (!container) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const content = doc.querySelector(SELECTORS.sectionContent);

  container.innerHTML = content?.outerHTML ?? html;
}

/**
 * Renders an error message.
 */
function renderError() {
  const container = document.querySelector(SELECTORS.content);
  if (container) {
    container.innerHTML = `
      <div class="api-product-modal__error">
        <p>Unable to load product information. Please try again.</p>
      </div>
    `;
  }
}

/**
 * Executes scripts within a container (innerHTML doesn't trigger script execution).
 *
 * @param {Element} container
 */
function executeScripts(container) {
  container.querySelectorAll('script').forEach((script) => {
    const newScript = document.createElement('script');
    [...script.attributes].forEach((attr) => newScript.setAttribute(attr.name, attr.value));
    newScript.textContent = script.textContent;
    script.replaceWith(newScript);
  });
}

/**
 * Observes modal content changes and executes scripts when content is added.
 */
function observeContent() {
  const content = document.querySelector(SELECTORS.content);
  if (!content) return;

  const observer = new MutationObserver((mutations) => {
    const hasNewContent = mutations.some((m) => m.type === 'childList' && m.addedNodes.length > 0);
    if (hasNewContent) {
      executeScripts(content);
      observer.disconnect();
    }
  });

  observer.observe(content, { childList: true, subtree: true });
}

/**
 * Observes wrapper visibility and starts content observer when modal opens.
 */
function observeWrapper() {
  const wrapper = document.querySelector(SELECTORS.wrapper);
  if (!wrapper) return;

  // Modal already visible
  if (!wrapper.classList.contains(HIDDEN_CLASS)) {
    observeContent();
    return;
  }

  // Watch for modal to open
  const observer = new MutationObserver(() => {
    if (!wrapper.classList.contains(HIDDEN_CLASS)) {
      observeContent();
    }
  });

  observer.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
}

// Wait for wrapper to exist, then start observing
const initInterval = setInterval(() => {
  if (document.querySelector(SELECTORS.wrapper)) {
    clearInterval(initInterval);
    observeWrapper();
  }
}, 100);

// Listen for product-modal:open events
document.addEventListener('product-modal:open', (event) => {
  const { handle } = /** @type {CustomEvent<{handle: string}>} */ (event).detail;
  openProductModal(handle);
});
